import React from 'react';
import Layout from './Layout';
import { usePageMeta } from './shared';
import { getServiceAvailability, sponsors } from './sponsors';

const EMAIL = 'Daniel@ourcloudnetwork.com';

function formatAvailabilityDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function AvailabilityTable({ at, bookings = sponsors }) {
  const availability = getServiceAvailability(at, bookings);

  return (
    <div className="border border-gray-200 dark:border-gray-700">
      <table className="w-full table-fixed text-left">
        <caption className="sr-only">Current Microsoft service sponsorship availability</caption>
        <thead className="bg-gray-100 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr>
            <th scope="col" className="w-[42%] px-3 sm:px-4 py-3 font-semibold">Microsoft service</th>
            <th scope="col" className="w-[27%] px-3 sm:px-4 py-3 font-semibold">Status</th>
            <th scope="col" className="w-[31%] px-3 sm:px-4 py-3 font-semibold">Next available</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
          {availability.map(row => (
            <tr key={row.service}>
              <th scope="row" className="px-3 sm:px-4 py-4 text-sm font-semibold text-gray-900 dark:text-gray-50 break-words">
                {row.service.trim()}
              </th>
              <td className="px-3 sm:px-4 py-4">
                <span className={`inline-flex px-2.5 py-1 text-xs font-semibold border ${
                  row.available
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                    : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
                }`}>
                  {row.available ? 'Available' : 'Sponsored'}
                </span>
              </td>
              <td className="px-3 sm:px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                {row.nextAvailableAt ? formatAvailabilityDate(row.nextAvailableAt) : 'Available now'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Sponsor() {
  usePageMeta({
    title: 'Sponsor a Microsoft Service | MS Message Center',
    description: 'Exclusively sponsor Microsoft Teams, Entra, Exchange Online, SharePoint Online, or Microsoft 365 Apps message pages.',
    url: 'https://msmessagecenter.com/sponsor',
  });

  return (
    <Layout>
      <article className="max-w-screen-lg mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-wide mb-2">Support the project</p>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50 mb-3">Sponsor a Microsoft service</h1>
        <p className="max-w-3xl text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
          Organisations can exclusively sponsor message pages for a service such as Microsoft Teams or Microsoft Entra. Each page carries one clearly labelled sponsor banner, and only one organisation can sponsor a service at a time.
        </p>

        <section aria-labelledby="packages-heading" className="mb-10">
          <h2 id="packages-heading" className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-4">Sponsorship packages</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              ['3 months', '$200'],
              ['6 months', '$400'],
              ['12 months', '$750'],
            ].map(([term, price]) => (
              <div key={term} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-hard px-5 py-4">
                <dt className="text-sm text-gray-500 dark:text-gray-400">{term}</dt>
                <dd className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1">{price}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="availability-heading" className="mb-10">
          <div className="mb-4">
            <h2 id="availability-heading" className="text-xl font-bold text-gray-900 dark:text-gray-50">Current availability</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Availability is updated when sponsorship bookings are confirmed.</p>
          </div>
          <AvailabilityTable />
        </section>

        <section aria-labelledby="booking-heading" className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-6">
          <h2 id="booking-heading" className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-3">Book a service</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
            Email me first at <a href={`mailto:${EMAIL}`} className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">{EMAIL}</a> to confirm the service and dates. We can then discuss the booking and banner artwork.
          </p>
          <a
            href="https://github.com/sponsors/DanielBradley1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            GitHub Sponsors
          </a>
        </section>
      </article>
    </Layout>
  );
}